"use client";

import React, {
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from "react";
import { useLongPress } from "./useLongPress";
import type { ThresholdDuration } from "./useLongPress";
import { Box, Spinner } from "@chakra-ui/react";
import GenericIcon, {
  type GenericIconType,
} from "../actions/GenericIcon";
import { iconSizePresets } from "@/shared/config/iconSizePresets";

export type GenericLongPressEffectProps = {
  isLongPressing: boolean;
  isVisible: boolean;
  icon?: GenericIconType;
  size?: string;
  bgColor?: string;
  spinnerDelay?: number; // Delay before showing spinner (ms)
};

/**
 * Visual indicator for long press functionality
 * Shows timer icon when idle, spinner when long pressing (with delay to avoid flashing on quick clicks)
 */
function GenericLongPressEffect({
  isLongPressing,
  isVisible,
  icon = "wait",
  size = "14px",
  bgColor = "moveon.secondaryLight",
  spinnerDelay = 100, // Default 100ms delay before showing spinner
}: GenericLongPressEffectProps) {
  const [spinnerReady, setSpinnerReady] = useState(false);

  // Delay showing spinner to avoid flashing on quick clicks.
  useEffect(() => {
    if (!isLongPressing) {
      return;
    }

    const timer = setTimeout(() => {
      setSpinnerReady(true);
    }, spinnerDelay);

    return () => {
      clearTimeout(timer);
      setSpinnerReady(false);
    };
  }, [isLongPressing, spinnerDelay]);

  if (!isVisible) return null;

  return (
    <Box
      position="absolute"
      top="0"
      right="0"
      bg={bgColor}
      color="white"
      borderRadius="full"
      w={size}
      h={size}
      display="flex"
      alignItems="center"
      justifyContent="center"
      pointerEvents="none"
    >
      {isLongPressing && spinnerReady ? (
        <Spinner size="xs" color="white" />
      ) : (
        <Box transform="scale(0.6)">
          <GenericIcon
            size={iconSizePresets.inline}
            icon={icon}
            color="white"
          />
        </Box>
      )}
    </Box>
  );
}

export type WithLongPressProps = {
  children: React.ReactElement;
  onLongPress: () => void;
  enabled?: boolean;
  threshold?: ThresholdDuration;
  preventClickAfterLongPress?: boolean;
  icon?: GenericIconType;
  longPressHint?: string;
  showEffect?: boolean;
  effectSize?: string;
  effectBgColor?: string;
  triggerOnClick?: (event: React.MouseEvent) => void;
};

/**
 * Wrapper component that adds long press functionality to a child component
 * Automatically handles pointer events and visual feedback
 *
 * @example
 * <WithLongPress onLongPress={handleReset} enabled={isActive}>
 *   <Button onClick={handleClick}>
 *     <GenericIcon icon="clear" />
 *   </Button>
 * </WithLongPress>
 */
function WithLongPress({
  children,
  // Long press config
  onLongPress,
  enabled = true,
  threshold,
  preventClickAfterLongPress = true,
  // Visual effect props
  icon = "wait",
  longPressHint,
  showEffect = true,
  effectSize = "14px",
  effectBgColor = "moveon.secondaryLight",
}: WithLongPressProps) {
  const longPressHandler = useLongPress({
    onLongPress,
    enabled,
    threshold,
    preventClickAfterLongPress,
    longPressHint,
  });

  if (!isValidElement(children)) {
    return children;
  }

  // Extract original handlers and props
  const childProps = children.props as {
    onClick?: (e: React.MouseEvent) => void;
    onPointerDown?: (e: React.PointerEvent) => void;
    onPointerUp?: (e: React.PointerEvent) => void;
    onPointerLeave?: (e: React.PointerEvent) => void;
    title?: string;
    children?: React.ReactNode;
    [key: string]: unknown;
  };

  const originalOnClick = childProps.onClick;

  const mergedProps: Record<string, unknown> = {
    ...(children.props as Record<string, unknown>),
    position: "relative" as const,
    onPointerDown: (e: React.PointerEvent) => {
      if (longPressHandler.handlers.onPointerDown) {
        longPressHandler.handlers.onPointerDown(e);
      }
      if (childProps.onPointerDown) {
        childProps.onPointerDown(e);
      }
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (longPressHandler.handlers.onPointerUp) {
        longPressHandler.handlers.onPointerUp();
      }
      if (childProps.onPointerUp) {
        childProps.onPointerUp(e);
      }
    },
    onPointerLeave: (e: React.PointerEvent) => {
      if (longPressHandler.handlers.onPointerLeave) {
        longPressHandler.handlers.onPointerLeave();
      }
      if (childProps.onPointerLeave) {
        childProps.onPointerLeave(e);
      }
    },
    onClick: (e: React.MouseEvent) => {
      // First run long press handler (checks if should prevent)
      if (longPressHandler.handlers.onClick) {
        longPressHandler.handlers.onClick(e);
      }

      // If long press prevented, don't continue
      if (e.defaultPrevented) {
        return;
      }

      if (!e.defaultPrevented && originalOnClick) {
        originalOnClick(e);
      }
    },
    title: longPressHandler.handlers.title || childProps.title,
    children: (
      <>
        {childProps.children}
        {showEffect && enabled && (
          <GenericLongPressEffect
            isLongPressing={longPressHandler.isLongPressing}
            isVisible={enabled}
            size={effectSize}
            bgColor={effectBgColor}
            icon={icon}
          />
        )}
      </>
    ),
  };

  return cloneElement(children, mergedProps);
}

export default React.memo(WithLongPress);
